// Shared modal shell in the "TradePlayerChooser" style:
// dark vertical gradient panel, thin amber border, rounded-3xl, a decorative
// amber top accent bar, a red circular close button, and a centered
// icon-badge + title + subtitle header. Every game modal wears this skin so
// they all read as one system.
//
// Sizing/positioning is intentionally left to the caller via `className` so each
// modal keeps its current dimensions:
//   • Floating modals:      "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-[90%] max-w-md"
//   • Inside an existing scrim: "relative w-full max-w-xl max-h-[90vh]"
import classNames from "classnames"
import React from "react"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { useBackHandler } from "@/Hooks/useBackHandler"

interface ModalProps {
  children: React.ReactNode
  /** Position + size classes for the panel (kept per-modal to preserve current sizing). */
  className?: string
  /** Optional wrapper around the children (padding / scroll region). */
  contentClassName?: string
  /** When provided, shows the red circular close button in the top-right corner. */
  onClose?: () => void
  /**
   * Whether Android's back button presses `onClose` for you. On by default:
   * a modal that can be closed is a modal back is expected to close, and
   * handlers stack newest-first so a dialog over a dialog unwinds in order.
   * Opt out for a modal whose close is a decision the game is waiting on.
   */
  closeOnBack?: boolean
  closeTestId?: string
  /** Header — any provided part is rendered, TradePlayerChooser style. */
  icon?: React.ReactNode
  title?: React.ReactNode
  subtitle?: React.ReactNode
  /** Extra classes for the header block (e.g. tighter vertical padding). */
  headerClassName?: string
  /** "sm" shrinks the icon badge, title and paddings for content-dense modals. */
  headerSize?: "default" | "sm"
  /** Decorative amber top accent bar. Defaults to true. */
  accent?: boolean
  style?: React.CSSProperties
  "data-testid"?: string
}

// The panel skin — shared so it can be reused by modals that can't route through
// this component (e.g. ones needing a ref). Matches the Stock Exchange modal's
// look: a plain dark glass panel, no accent border/bar.
export const MODAL_SHELL_CLASS =
  "rounded-2xl border border-gray-700/60 bg-black/90 backdrop-blur-xl shadow-2xl"

// Kept as a no-op so existing callers don't need to be touched — the decorative
// amber top accent bar has been retired in favor of the plain Stock Exchange shell.
export const ModalTopAccent: React.FC = () => null

const Modal: React.FC<ModalProps> = ({
  children,
  className,
  contentClassName,
  onClose,
  closeOnBack = true,
  closeTestId,
  icon,
  title,
  subtitle,
  headerClassName,
  headerSize = "default",
  accent = true,
  style,
  "data-testid": testId,
}) => {
  const hasHeader = icon || title || subtitle
  const isSmallHeader = headerSize === "sm"

  useBackHandler(() => {
    onClose?.()
    return true
  }, !!onClose && closeOnBack)

  return (
    <div
      data-testid={testId}
      style={style}
      className={classNames(MODAL_SHELL_CLASS, className)}
    >
      <div className="rounded-2xl overflow-hidden">
        {accent && <ModalTopAccent />}

        {hasHeader && (
          <div
            className={classNames(
              "flex flex-col items-center px-8 shrink-0",
              isSmallHeader ? "pt-5 pb-3" : "pt-9 pb-6",
              headerClassName
            )}
          >
            {icon && (
              <div
                className={classNames(
                  "rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20",
                  isSmallHeader ? "w-10 h-10 mb-2" : "w-14 h-14 mb-4"
                )}
              >
                {icon}
              </div>
            )}
            {title && (
              <h2
                className={classNames(
                  "font-poppins font-bold text-white tracking-wide text-center",
                  isSmallHeader ? "text-lg" : "text-2xl"
                )}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                className={classNames(
                  "text-white/40 font-poppins text-center",
                  isSmallHeader ? "text-xs mt-0.5" : "text-sm mt-1"
                )}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}

        {contentClassName ? (
          <div className={contentClassName}>{children}</div>
        ) : (
          children
        )}
      </div>

      {onClose && (
        <div className="absolute top-4 right-4 z-20">
          <ModalCloseButton onClick={onClose} data-testid={closeTestId} />
        </div>
      )}
    </div>
  )
}

export default Modal
